import type { RoomFilterType } from '@/scopes/account/group-responsible/domain/types'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { RoomFilterValues } from '@/scopes/account/group-responsible/domain/types'
import { useGroupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/useGroupResponsibleStore'
import { getFilteredRooms, getRoomsCounts } from '../selectors'
import { EditRoomSheetPortal } from './EditRoomSheetPortal'
import { FilterToggleGroup } from './FilterToggleGroup'
import { RoomAssignmentStatusBanner } from './RoomAssignmentStatusBanner'
import { RoomExtraInfoSheetPortal } from './RoomExtraInfoSheetPortal'
import { RoomStagingToolbar } from './RoomStagingToolbar'
import { RoomTableRow } from './RoomTableRow'
import {
  EmptyTableMessage,
  Table,
  TableHead,
  TableRow,
} from './Table'
import { TableSearch } from './TableSearch'

const breakpointHideCol = 'primitives_breakpoint_m_max'

const FILTERS: { value: RoomFilterType, label: string, countKey: keyof ReturnType<typeof getRoomsCounts> }[] = [
  { value: RoomFilterValues.ALL, label: 'groups_responsible_all', countKey: 'all' },
  { value: RoomFilterValues.NOT_OCCUPIED, label: 'groups_responsible_not_occupied', countKey: 'notOccupied' },
  { value: RoomFilterValues.OCCUPIED, label: 'groups_responsible_occupied', countKey: 'occupied' },
]

export function RoomsTable() {
  const [filter, setFilter] = React.useState<RoomFilterType>('all')
  const [searchTerm, setSearchTerm] = React.useState('')
  const counts = useGroupResponsibleStore(getRoomsCounts)
  const filteredRooms = useGroupResponsibleStore(state => getFilteredRooms(state, filter, searchTerm))
  const intl = useIntl()
  const changeExtraInfoId = useGroupResponsibleStore(state => state.changeExtraInfoId)

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value)
  }

  return (
    <>
      <TableSearch searchTerm={searchTerm} onSearchChange={handleSearchChange} />
      <FilterToggleGroup filter={filter} filters={FILTERS} counts={counts} onFilterChange={setFilter} />
      <RoomAssignmentStatusBanner />
      {filteredRooms.length === 0
        ? (
            <EmptyTableMessage />
          )
        : (
            <Table>
              <thead>
                <TableRow>
                  <TableHead>
                    <Typography variant="body-s-regular-tight">
                      {intl.formatMessage({ id: 'groups_responsible_table_room_name_label' })}
                    </Typography>
                  </TableHead>
                  <TableHead $hideOn={breakpointHideCol}>
                    <Typography variant="body-s-regular-tight">
                      {intl.formatMessage({ id: 'groups_responsible_room_type' })}
                    </Typography>
                  </TableHead>
                  <TableHead $alignRight>
                    <Typography variant="body-s-regular-tight">
                      {intl.formatMessage({ id: 'groups_responsible_table_room_occupancy_label' })}
                    </Typography>
                  </TableHead>
                  <TableHead $hideOn={breakpointHideCol} />
                </TableRow>
              </thead>
              <tbody>
                {filteredRooms.map(room => (
                  <RoomTableRow
                    key={room.id}
                    room={room}
                    onClick={() => changeExtraInfoId(room.id)}
                  />
                ))}
              </tbody>
            </Table>
          )}
      <RoomExtraInfoSheetPortal />
      <EditRoomSheetPortal />
      <RoomStagingToolbar />
    </>
  )
}
